import type { Tag } from '@/features/tags/public';
import type {
  Task,
  TaskDetail,
  TaskSummaryDto,
  TaskDetailDto,
  CreateTaskPayload,
  UpdateTaskDetailPayload,
  CreateSubtaskPayload,
  CreateTaskApiRequest,
  UpdateTaskApiRequest,
  CreateSubtaskApiRequest,
  TaskAttachment,
} from '../types';
import type { PlannedDate } from '@/shared/types/datetime';
import { priorityFromInt, priorityToInt } from './priority.mapper';
import { statusTypeFromApi } from './status-type.mapper';

/**
 * Normalize an inbound date field to a `PlannedDate`.
 *
 * The backend always returns the object shape, but a legacy ISO string is
 * coerced to a date-only `PlannedDate` so mixed data during rollout (and mock
 * fixtures) never break reads.
 */
function toPlannedDate(value: PlannedDate | string | null | undefined): PlannedDate | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    return { date: value.split('T')[0] };
  }
  return value;
}

const ASSIGNEE_COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#a855f7',
  '#ec4899',
];

function deterministicColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % ASSIGNEE_COLORS.length;
  return ASSIGNEE_COLORS[index];
}

function adaptAssignee(
  dto: { id: string; displayName?: string | null; avatarUrl?: string | null } | null,
) {
  if (!dto) return null;
  return {
    id: dto.id,
    name: dto.displayName ?? '—',
    avatarUrl: dto.avatarUrl ?? undefined,
    color: deterministicColor(dto.id),
  };
}

/**
 * Inbound: API TaskSummaryDto → UI Task.
 */
function resolvePriority(value: number | string | null | undefined): Task['priority'] {
  if (value == null) return undefined;
  if (typeof value === 'number') return priorityFromInt(value);
  if (value === 'None') return undefined;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : priorityFromInt(parsed);
}

export function adaptTaskSummaryDto(dto: TaskSummaryDto): Task {
  return {
    id: dto.id,
    title: dto.title,
    tagIds: dto.tagIds ?? [],
    description: undefined,
    status: dto.status
      ? {
          id: dto.status.id,
          name: dto.status.name,
          type: statusTypeFromApi(dto.status.type),
          color: dto.status.color ?? '#94a3b8',
          order: dto.order,
          boardId: dto.board?.id ?? '',
        }
      : {
          id: '',
          name: '—',
          type: 'todo',
          color: '#94a3b8',
          order: dto.order,
          boardId: '',
        },
    priority: resolvePriority(dto.priority),
    assignee: adaptAssignee(dto.assignee),
    dueDate: toPlannedDate(dto.dueDate),
    startDate: toPlannedDate(dto.startDate),
    estimatedMinutes: dto.estimatedHours != null ? Math.round(dto.estimatedHours * 60) : null,
    totalTrackedHours: dto.totalTrackedHours ?? undefined,
    projectId: dto.project?.id ?? '',
    groupId: dto.group?.id ?? undefined,
    parentId: dto.parent?.id ?? null,
    subtaskCount: dto.subtaskCount ?? 0,
    isCompleted: dto.isCompleted,
    isArchived: dto.isArchived,
    order: dto.order,
  };
}

/**
 * Inbound: API TaskDetailDto + optional project tags → UI TaskDetail.
 */
export function adaptTaskDetailDto(dto: TaskDetailDto, projectTags?: Tag[]): TaskDetail {
  const base = adaptTaskSummaryDto(dto);

  const tagIds = new Set<string>();
  // TaskTagDto join happens outside; here we just prepare shape.
  // Caller should pass hydrated tags if available.
  const tags: Tag[] = projectTags?.filter((t) => t.id != null && tagIds.has(t.id)) ?? [];

  const attachments: TaskAttachment[] = []; // hydrated via separate endpoint

  return {
    ...base,
    description: dto.description ?? undefined,
    tags,
    attachments,
    estimatedMinutes: base.estimatedMinutes ?? null,
    timer: { seconds: 0, running: false },
    createdBy: adaptAssignee(dto.createdBy),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt ?? undefined,
  };
}

/**
 * Outbound: UI CreateTaskPayload → API CreateTaskApiRequest.
 */
export function adaptCreatePayload(payload: CreateTaskPayload): CreateTaskApiRequest {
  const result: CreateTaskApiRequest = {
    title: payload.title,
    description: payload.description ?? null,
    boardId: payload.boardId,
    statusId: payload.statusId ?? null,
    groupId: payload.groupId ?? null,
    assigneeId: payload.assigneeId ?? null,
    startDate: payload.startDate ?? null,
    dueDate: payload.dueDate ?? null,
    estimatedHours: payload.estimatedMinutes != null ? payload.estimatedMinutes / 60 : null,
  };

  if (payload.priority !== undefined) {
    result.priority = priorityToInt(payload.priority);
  }

  return result;
}

/**
 * Outbound: UI UpdateTaskDetailPayload → split core vs side-effects.
 */
export function adaptUpdatePayload(payload: UpdateTaskDetailPayload): {
  core: UpdateTaskApiRequest;
  sideEffects: {
    priority?: number;
    statusId?: string;
    groupId?: string | null;
    assigneeId?: string | null;
  };
} {
  const core: UpdateTaskApiRequest = {};
  if (payload.title !== undefined) {
    core.title = payload.title ?? null;
  }
  if (payload.description !== undefined) {
    core.description = payload.description ?? null;
  }
  if (payload.startDate !== undefined) {
    core.startDate = payload.startDate ?? null;
  }
  if (payload.dueDate !== undefined) {
    core.dueDate = payload.dueDate ?? null;
  }
  if (payload.estimatedMinutes !== undefined) {
    core.estimatedHours = payload.estimatedMinutes != null ? payload.estimatedMinutes / 60 : null;
  }

  const sideEffects: ReturnType<typeof adaptUpdatePayload>['sideEffects'] = {};
  if (payload.priority !== undefined) {
    sideEffects.priority = priorityToInt(payload.priority);
  }
  if (payload.statusId !== undefined) {
    sideEffects.statusId = payload.statusId;
  }
  if (payload.groupId !== undefined) {
    sideEffects.groupId = payload.groupId;
  }
  if (payload.assigneeId !== undefined) {
    sideEffects.assigneeId = payload.assigneeId;
  }

  return { core, sideEffects };
}

/**
 * Outbound: UI CreateSubtaskPayload → API CreateSubtaskApiRequest.
 */
export function adaptCreateSubtaskPayload(payload: CreateSubtaskPayload): CreateSubtaskApiRequest {
  return {
    title: payload.name,
    description: null,
  };
}
